import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaCheckCircle, FaCopy, FaSpinner } from "react-icons/fa";
import axios from "axios";
import Header from "../components/Header";

const defaultThankYou = {
  title: "Thank you!",
  message:
    "Your feedback has been submitted successfully. Thank you for helping us improve future visits.",
  reward: null,
};

const readSavedDetails = () => {
  try {
    return JSON.parse(sessionStorage.getItem("feedbackThankYou")) || {};
  } catch (error) {
    return {};
  }
};

export default function ThankYou() {
  const location = useLocation();
  const queryToken = new URLSearchParams(location.search).get("token") || "";
  const savedDetails = useMemo(() => {
    const saved = readSavedDetails();
    return !queryToken || saved.feedbackToken === queryToken ? saved : {};
  }, [queryToken]);
  const [bookingReference, setBookingReference] = useState(
    location.state?.bookingReference || savedDetails.bookingReference || "",
  );
  const [thankYou, setThankYou] = useState(
    location.state?.thankYou || savedDetails.thankYou || defaultThankYou,
  );
  const [loading, setLoading] = useState(
    !location.state?.thankYou && !savedDetails.thankYou && Boolean(queryToken),
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "Thank You - Thorney Park Golf Club";
  }, []);

  useEffect(() => {
    if (location.state?.thankYou || savedDetails.thankYou || !queryToken) {
      return;
    }

    const loadThankYou = async () => {
      try {
        const apiUrl =
          process.env.REACT_APP_API_URL || "http://localhost:1337/api/";
        const response = await axios.get(`${apiUrl}feedback-thank-you`, {
          params: { token: queryToken },
        });
        if (response.data.success && response.data.thankYou) {
          setThankYou(response.data.thankYou);
          setBookingReference(response.data.bookingReference || "");
          sessionStorage.setItem(
            "feedbackThankYou",
            JSON.stringify({
              bookingReference: response.data.bookingReference || "",
              feedbackToken: queryToken,
              thankYou: response.data.thankYou,
            }),
          );
        }
      } catch (error) {
        console.error("Unable to reload feedback thank-you details:", error);
      } finally {
        setLoading(false);
      }
    };

    loadThankYou();
  }, [location.state, queryToken, savedDetails]);

  const reward = thankYou?.reward;
  const discountLabel = reward
    ? reward.discountType === "percentage"
      ? `${reward.discountAmount}% off`
      : `£${Number(reward.discountAmount).toFixed(2)} off`
    : "";

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(reward.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Unable to copy coupon code:", error);
    }
  };

  return (
    <>
      <Header />
      <main className="flex min-h-[60vh] items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
        <section className="w-full max-w-xl rounded-lg bg-white px-6 py-10 text-center shadow-lg ring-1 ring-gray-200 sm:px-10">
          <FaCheckCircle
            className="mx-auto text-[#17BA17]"
            size={64}
            aria-hidden="true"
          />
          <p className="mt-6 text-sm font-semibold uppercase tracking-wide text-[#0f6f2f]">
            Feedback received
          </p>
          {loading ? (
            <div className="flex justify-center py-10 text-[#17BA17]">
              <FaSpinner className="animate-spin" size={28} />
            </div>
          ) : (
            <>
              <h1 className="mt-2 text-3xl font-bold text-gray-900">
                {thankYou?.title || defaultThankYou.title}
              </h1>
              <p className="mt-4 whitespace-pre-line text-base leading-7 text-gray-600">
                {thankYou?.message || defaultThankYou.message}
              </p>

              {reward && (
                <div className="mt-8 rounded-lg border-2 border-dashed border-[#17BA17] bg-green-50 p-5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-[#0f6f2f]">
                    A reward for your next booking
                  </p>
                  <p className="mt-2 text-2xl font-bold text-gray-900">
                    {discountLabel}
                  </p>
                  <div className="mx-auto mt-4 flex max-w-sm items-center overflow-hidden rounded-md border border-green-300 bg-white">
                    <span className="flex-1 px-4 py-3 text-lg font-bold tracking-widest text-[#0f6f2f]">
                      {reward.code}
                    </span>
                    <button
                      type="button"
                      onClick={copyCode}
                      className="inline-flex items-center self-stretch bg-[#17BA17] px-4 font-semibold text-white transition hover:bg-[#129c12] focus:outline-none focus:ring-2 focus:ring-[#17BA17]/40"
                    >
                      <FaCopy className="mr-2" />
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <div className="mt-4 space-y-1 text-sm text-gray-600">
                    {reward.minimumBookingAmount > 0 && (
                      <p>
                        Minimum booking value: £
                        {Number(reward.minimumBookingAmount).toFixed(2)}
                      </p>
                    )}
                    {reward.expiryDate && <p>Valid until: {reward.expiryDate}</p>}
                    {reward.terms && <p>{reward.terms}</p>}
                  </div>
                </div>
              )}

              {bookingReference && (
                <p className="mt-5 text-sm text-gray-500">
                  Booking reference: {bookingReference}
                </p>
              )}
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                {reward && (
                  <Link
                    to={`/booking?coupon=${encodeURIComponent(reward.code)}`}
                    className="inline-flex items-center justify-center rounded-md bg-[#17BA17] px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-[#129c12] focus:outline-none focus:ring-2 focus:ring-[#17BA17]/40 focus:ring-offset-2"
                  >
                    Book again
                  </Link>
                )}
                <Link
                  to="/"
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#17BA17]/40 focus:ring-offset-2"
                >
                  Return to home
                </Link>
              </div>
            </>
          )}
        </section>
      </main>
    </>
  );
}
